import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../../api';
import { fehlendeFunktion, upgradeAnbieten } from '../../billtoro/upgrade';
import { ConfirmDialog } from '../../components/Dialog';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { auftragPdfUrl } from '../AuftragDaten';
import { PdfDialog } from '../PdfDialog';
import { darf, fehlerText, GesperrtKnopf, NurAnsehen, tarifFehler } from '../tarifHinweis';
import type { Overview } from '../types';
import { BuchenDialog } from './BuchenDialog';
import { artText, mengeText, mengeVorzeichen, verlaufUrl, rueckgaengigUrl, type Bewegung, type Lager, type Verlauf } from './LagerDaten';
import './lager.css';

// Verlauf eines Artikels (/rechnung/lager/:id): Bestand groß, Mindestbestand, Bewegungen neueste zuerst (200 je Seite,
// „Ältere laden“) mit Bestand danach und Link zum Beleg. „Rückgängig“ nur bei Zugang, Abgang und Zählung von Hand, die
// noch gelten (Gegenbuchung mit dem heutigen Datum); Bewegungen aus Belegen ändern sich nur mit dem Beleg (Storno).
// Zugang aus einer Ausgabe: Marke und Hinweis, wenn die Ausgabe danach storniert oder geändert wurde (bucht nichts zurück).
// Kein Name des Logins (der Server schickt keinen).

export function LagerArtikel({ data, id, lager }: { data: Overview; id: string; lager: Lager }) {
  const toast = useToast();
  const tarif = data.tarif;
  const [v, setV] = useState<Verlauf | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [mehrBusy, setMehrBusy] = useState(false);
  const [buchen, setBuchen] = useState<'zugang' | 'abgang' | null>(null);
  const [weg, setWeg] = useState<Bewegung | null>(null);
  const [busy, setBusy] = useState(false);
  const [pdf, setPdf] = useState<{ url: string; title: string } | null>(null);
  const aktuell = useRef(id);
  aktuell.current = id;
  const pflegen = darf(tarif, 'kunden');
  const fehlt = fehlendeFunktion(tarif, 'lager');

  const laden = useCallback(async () => {
    try {
      const r = await api.get<Verlauf>(verlaufUrl(id));
      if (aktuell.current !== id) return;
      setV(r);
      setFehler(null);
    } catch (err) {
      if (aktuell.current === id) setFehler(fehlerText(err));
    }
  }, [id]);
  useEffect(() => {
    setV(null);
    void laden();
  }, [laden]);

  async function aeltere() {
    if (!v?.bewegungen.length) return;
    setMehrBusy(true);
    try {
      const r = await api.get<Verlauf>(verlaufUrl(id, v.bewegungen[v.bewegungen.length - 1]!.nr));
      setV((cur) => (cur ? { ...cur, bewegungen: [...cur.bewegungen, ...r.bewegungen], weitere: r.weitere } : cur));
    } catch (err) {
      toast(errorText(err));
    } finally {
      setMehrBusy(false);
    }
  }

  async function zuruecknehmen() {
    if (!weg) return;
    setBusy(true);
    try {
      await api.post(rueckgaengigUrl(weg.id), {});
      toast('Buchung zurückgenommen');
      setWeg(null);
      void laden();
      void lager.laden();
    } catch (err) {
      setWeg(null);
      if (!tarifFehler(err)) toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function belegAnsehen(b: NonNullable<Bewegung['beleg']>) {
    setPdf(
      b.art === 'lieferschein'
        ? { url: auftragPdfUrl('lieferschein', b.id), title: `Lieferschein ${b.number}` }
        : { url: `/api/rechnungstool/rechnung/${b.id}/pdf`, title: `${b.art === 'storno' ? 'Stornorechnung' : 'Rechnung'} ${b.number}` },
    );
  }

  if (!v) {
    return (
      <div className="lg">
        <p className="lg-zurueck">
          <Link to="/rechnung/lager">← Lager</Link>
        </p>
        {fehler ? <div className="notice bad">{fehler}</div> : <div className="spinner" aria-label="Lädt" />}
      </div>
    );
  }
  const a = v.artikel;
  const aktiv = a.lager && !a.entfernt;
  // Knöpfe zum Buchen: nur mit dem Recht; ohne Lager im Tarif mit Marke, öffnet „Mehr mit Billtoro“
  const knopf = (text: string, an: () => void, primary = false) =>
    fehlt ? (
      <GesperrtKnopf tarif={tarif} fehlt={fehlt} className={`btn${primary ? ' primary' : ''}`} onUpgrade={() => upgradeAnbieten(tarif, 'lager')}>
        {text}
      </GesperrtKnopf>
    ) : (
      <button type="button" className={`btn${primary ? ' primary' : ''}`} onClick={an}>
        {text}
      </button>
    );
  const bestandKlasse = a.bestand < 0 ? ' bad' : a.wenig ? ' warn' : '';

  return (
    <div className="lg">
      <p className="lg-zurueck">
        <Link to="/rechnung/lager">← Lager</Link>
      </p>
      <div className="rt-head lg-kopf">
        <div>
          <h1>
            {a.name}
            <NurAnsehen tarif={tarif} bereich="kunden" />
          </h1>
          <p className="muted">
            {a.artikelnummer ? <span className="num">{a.artikelnummer}</span> : 'Ohne Artikelnummer'}
            {a.mindest !== null && <> · Mindestbestand {mengeText(a.mindest, a.unit)}</>}
          </p>
        </div>
        <div className={`lg-gross${bestandKlasse}`}>
          <span className="lg-gross-l">Bestand</span>
          <b className="num">{mengeText(a.bestand, a.unit)}</b>
        </div>
      </div>
      {aktiv && pflegen && (
        <div className="lg-knoepfe lg-leiste">
          {knopf('Zugang', () => setBuchen('zugang'), true)}
          {knopf('Abgang', () => setBuchen('abgang'))}
          {fehlt ? (
            knopf('Zählen', () => undefined)
          ) : (
            <Link to={`/rechnung/lager/zaehlen?artikel=${a.id}`} className="btn">
              Zählen
            </Link>
          )}
        </div>
      )}
      {a.entfernt ? (
        <p className="notice info">Dieser Artikel ist aus der Liste entfernt. Der Verlauf im Lager bleibt gespeichert.</p>
      ) : (
        !a.lager && <p className="notice info">Für diesen Artikel führt Billtoro keinen Bestand mehr. Der Verlauf bleibt gespeichert.</p>
      )}
      {aktiv && v.neuZaehlen && (
        <p className="notice warn">
          Bitte neu zählen: Während „Bestand führen“ aus war, kamen Rechnungen oder Lieferscheine mit diesem Artikel dazu. Sie haben den Bestand nicht
          geändert.
        </p>
      )}

      {v.bewegungen.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>Noch keine Bewegung. Buchen Sie einen Zugang oder zählen Sie den Bestand.</p>
        </div>
      ) : (
        <>
          <div className="lg-vk" aria-hidden="true">
            <span>Datum</span>
            <span>Art</span>
            <span className="r">Menge</span>
            <span className="r">Bestand danach</span>
            <span>Notiz</span>
            <span />
          </div>
          <ul className="lg-verlauf" aria-label="Verlauf">
            {v.bewegungen.map((b) => (
              <li key={b.id} className={`lg-vz${b.zurueckgenommen ? ' zurueck' : ''}`}>
                <span className="lg-vz-d">{fmtDate(b.datum)}</span>
                <span className="lg-vz-art">
                  {b.beleg ? (
                    <button type="button" className="linkbtn" onClick={() => belegAnsehen(b.beleg!)}>
                      {artText(b)}
                    </button>
                  ) : (
                    artText(b)
                  )}
                  {b.zurueckgenommen && <span className="badge b-none">zurückgenommen</span>}
                  {b.ausgabe?.storniert && <span className="badge b-warn">Ausgabe {b.ausgabe.nummer} storniert</span>}
                  {b.ausgabe && !b.ausgabe.storniert && b.ausgabe.geaendert && <span className="badge b-warn">Ausgabe {b.ausgabe.nummer} geändert</span>}
                </span>
                <span className="lg-vz-m num r">
                  {mengeVorzeichen(b.menge)}
                  {b.gezaehlt !== null && <small className="muted"> gezählt {mengeText(b.gezaehlt, b.einheit)}</small>}
                </span>
                <span className="lg-vz-b num r">{mengeText(b.bestandDanach, b.einheit)}</span>
                <span className="lg-vz-n">{b.notiz}</span>
                <span className="lg-vz-act">
                  {b.rueckgaengig && pflegen && (
                    <button type="button" className="linkbtn" onClick={() => (fehlt ? upgradeAnbieten(tarif, 'lager') : setWeg(b))}>
                      Rückgängig
                    </button>
                  )}
                </span>
                {b.abweichung && (
                  <p className="lg-vz-hinweis">Seit dieser Zählung kamen Belege mit früherem Datum dazu; der Bestand am Zähltag weicht deshalb ab.</p>
                )}
                {/* Zugang aus einer Ausgabe, die danach storniert oder geändert wurde: nur Hinweis, nichts wird zurückgebucht */}
                {b.ausgabe && !b.zurueckgenommen && (b.ausgabe.storniert || b.ausgabe.geaendert) && (
                  <p className="lg-vz-hinweis">
                    {b.ausgabe.storniert ? 'Die Ausgabe wurde nach diesem Zugang storniert.' : 'Die Ausgabe wurde nach diesem Zugang geändert.'} Billtoro
                    bucht nichts von selbst zurück. Stimmt der Bestand nicht mehr, nehmen Sie den Zugang mit „Rückgängig“ zurück.
                  </p>
                )}
              </li>
            ))}
          </ul>
          {v.weitere && (
            <p className="lg-mehr">
              <button type="button" className="btn" disabled={mehrBusy} onClick={() => void aeltere()}>
                Ältere laden
              </button>
            </p>
          )}
        </>
      )}

      {aktiv && lager.liste && (
        <BuchenDialog
          open={!!buchen}
          art={buchen ?? 'zugang'}
          firmId={data.firm.id}
          heute={data.today}
          artikel={lager.liste.artikel}
          start={a.id}
          onClose={() => setBuchen(null)}
          onGebucht={() => {
            void laden();
            void lager.laden();
          }}
        />
      )}
      <ConfirmDialog
        open={!!weg}
        title="Buchung zurücknehmen?"
        text={
          weg
            ? `${artText(weg)} vom ${fmtDate(weg.datum)} (${mengeVorzeichen(weg.menge)} ${weg.einheit}): Billtoro bucht die Menge mit dem heutigen Datum zurück. Beide Buchungen bleiben im Verlauf.`
            : undefined
        }
        confirmLabel="Ja, zurücknehmen"
        busy={busy}
        onConfirm={() => void zuruecknehmen()}
        onCancel={() => setWeg(null)}
      />
      <PdfDialog url={pdf?.url ?? null} title={pdf?.title ?? ''} download={pdf ? `${pdf.url}?download=1` : undefined} onClose={() => setPdf(null)} />
    </div>
  );
}
